import Footer from "components/Footer/Footer.jsx";
import ScrollToTop from "components/ScrollToTop";
import TrialEndingBanner from "components/TrialEndingBanner";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import routes from "routes.js";



import AdminNavbar from "../../custom_modules/AdminNavbar";
import OnboardingWrapper from "../../custom_modules/OnboardingNew";
import Sidebar from "../../custom_modules/Sidebar";
import { addPath } from "../../filterReducer";
import { PrivateRoute } from "../../modules/privateRoute";
import { WeekConfigProvider, useWeekConfig } from "../../utils/weekConfig";


const AdminContent = props => {
  const dispatch = useDispatch();
  const { isWeekStartReady } = useWeekConfig();

  const [sidebarMini, setSidebarMini] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [filteredRoutes, setFilteredRoutes] = useState(routes);
  const [daysLeftInTrial, setDaysLeftInTrial] = useState(null);

  const adminHistory = useHistory();
  const location = useLocation();
  const wrapperRef = useRef(null);

  const { selectedOrganization } = props;

  // Check if current plan is free and user is owner
  const isFreePlan = useMemo(() => {
    const isFree =
      selectedOrganization?.plan?.name === "Free" ||
      selectedOrganization?.plan?.nick_name === "standard-free" ||
      selectedOrganization?.plan?.nick_name === "elite-free";
    const isOwner = selectedOrganization?.role === "owner";
    return isFree && isOwner;
  }, [
    selectedOrganization?.plan?.name,
    selectedOrganization?.plan?.nick_name,
    selectedOrganization?.role,
  ]);

  // Redirect based on plan for non-admin/owner
  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization.role !== "admin" &&
      selectedOrganization.role !== "owner" &&
      adminHistory?.location?.pathname === "/user/dashboard"
    ) {
      const isStandardPlan =
        selectedOrganization.plan &&
        (selectedOrganization.plan.name === "Standard" ||
          selectedOrganization.plan.nick_name === "standard-free");

      if (isStandardPlan) {
        adminHistory.push("/user/shift-and-schedule");
      } else {
        // Use "tasks" for operation type (default), "jobs" for service type
        const orgType = selectedOrganization.type || "operation";
        const tasksPath =
          orgType === "operation" ? "/user/tasks" : "/user/jobs";
        adminHistory.push(tasksPath);
      }
    }
  }, [selectedOrganization, adminHistory?.location?.pathname]);

  // Initialize Crisp chat for owners
  useEffect(() => {
    if (localStorage.getItem("selected_organization_role") === "owner") {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = "99c3a618-53c4-44b5-8e95-dc28db5a70a7";
      (function () {
        var d = document;
        var s = d.createElement("script");
        s.src = "https://client.crisp.chat/l.js";
        s.async = 1;
        d.getElementsByTagName("head")[0].appendChild(s);
      })();
      if (localStorage.getItem("user_fullName")) {
        window.$crisp.push([
          "set",
          "user:nickname",
          localStorage.getItem("user_fullName"),
        ]);
      }
      if (localStorage.getItem("email")) {
        window.$crisp.push([
          "set",
          "user:email",
          localStorage.getItem("email"),
        ]);
      }
    }
  }, []);

  // Fetch subscription and calculate remaining trial days
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      props.getOrganizationSubscription({
        organization_id: selectedOrganization.id,
      });
    }
  }, [selectedOrganization?.id]);

  // Calculate remaining trial days from subscription data
  useEffect(() => {
    if (props.currentSubscription && props.currentSubscription.trial_till) {
      const trialEndDate = moment(props.currentSubscription.trial_till);
      const today = moment();
      const daysRemaining = trialEndDate.diff(today, "days");
      
      if (daysRemaining < 0) {
        setDaysLeftInTrial(null); // null means trial has ended
      } else {
        setDaysLeftInTrial(daysRemaining);
      }
    }
  }, [props.currentSubscription]);

  // Determine display message and button visibility
  const trialEndingBannerProps = (() => {
    let displayMessage;
    if (daysLeftInTrial === null) {
      displayMessage = "Free trial has ended";
    } else if (daysLeftInTrial === 0) {
      displayMessage = "Free trial ends today";
    } else if (daysLeftInTrial === 1) {
      displayMessage = "Free trial ends tomorrow";
    } else {
      displayMessage = `Free trial ends in ${daysLeftInTrial} days`;
    }
    
    return {
      displayMessage,
      showButton: true, // Always show button when banner is shown
    };
  })();

  // Save organization config to localStorage
  useEffect(() => {
    if (selectedOrganization?.plan?.id) {
      localStorage.setItem(
        "selected_organization_plan",
        selectedOrganization.plan.name,
      );
    }
    if (selectedOrganization?.configuration?.id) {
      localStorage.setItem(
        "date_format",
        selectedOrganization.configuration.date_format,
      );
      localStorage.setItem(
        "start_day_of_week",
        selectedOrganization.configuration.start_day_of_week,
      );
    }
  }, [selectedOrganization]);

  // Scroll to top on route change
  useEffect(() => {
    return adminHistory.listen((location, action) => {
      if (action === "PUSH" && wrapperRef?.current) {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
        wrapperRef.current.scrollTop = 0;
      }
    });
  }, [adminHistory]);

  // Filter routes for projects and jobs based on organization type
  useEffect(() => {
    if (!selectedOrganization || !selectedOrganization.id) {
      setFilteredRoutes(routes);
      return;
    }

    const orgType = selectedOrganization.type || "operation";

    const filterRoutes = routesArr => {
      return routesArr
        .map(route => {
          if (route.collapse && route.views) {
            return { ...route, views: filterRoutes(route.views) };
          }
          return route;
        })
        .filter(route => {
          if (route.collapse && route.views && route.views.length === 0)
            return false;
          // If route has no type, include it (common routes)
          if (!route.type) return true;
          // Check if route type matches organization type
          return route.type === orgType;
        });
    };

    const allowedRoutes = filterRoutes(routes);
    setFilteredRoutes(allowedRoutes);
  }, [selectedOrganization?.id, selectedOrganization?.type]);

  const getRoutes = routesArray => {
    const routes = [];
    routesArray.forEach((prop, key) => {
      if (prop.collapse && prop.views) {
        // Flatten nested routes from collapse views
        const nestedRoutes = getRoutes(prop.views);
        routes.push(...nestedRoutes);
      } else if (prop.layout === "/user") {
        routes.push(
          <PrivateRoute
            key={prop.path || key}
            path={prop.layout + prop.path}
            restricted={prop.restricted}
            selectedOrganization={selectedOrganization}
            component={prop.component}
          />,
        );
      }
    });
    return routes;
  };

  const handleMiniClick = () => {
    setSidebarMini(!sidebarMini);
    document.body.classList.toggle("sidebar-mini");
  };

  const handleNavOpen = () => setNavOpen(!navOpen);
  const clearNavOpen = () => setNavOpen(false);

  const handleUpgradeClick = () => {
    adminHistory.push("/user/subscription");
  };

  useEffect(() => {
    dispatch(addPath(location.pathname));
  }, [location.pathname, dispatch]);

  if (!isWeekStartReady) {
    return <div style={{ padding: "2rem", textAlign: "center" }} />;
  }

  return (
    <OnboardingWrapper
      selectedOrganization={selectedOrganization}
      userRole={props.userRole}
      onInviteMember={props.onInviteMember}
      isInviteLoading={props.isInviteLoading}
      memberInvited={props.memberInvited}
      clearMemberInvited={props.clearMemberInvited}
      updateFirstLogin={props.updateFirstLogin}
      loginProfileCreatedReset={props.loginProfileCreatedReset}
    >
      {!isWeekStartReady ? (
        <div style={{ padding: "2rem", textAlign: "center" }}></div>
      ) : (
        <div className="wrapper" ref={wrapperRef}>
          <Sidebar
            {...props}
            routes={filteredRoutes}
            handleMiniClick={handleMiniClick}
            sidebarMini={sidebarMini}
            navOpen={navOpen}
            clearNavOpen={clearNavOpen}
            handleNavOpen={handleNavOpen}
          />
          <div className="main-panel">
            <AdminNavbar
              sidebarState={sidebarMini}
              {...props}
              handleMiniClick={handleMiniClick}
              handleNavOpen={handleNavOpen}
            />
            {(isFreePlan || props.currentSubscription?.status === "trialing" || (daysLeftInTrial === null && isFreePlan && props.currentSubscription?.trial_till)) && props.currentSubscription && (
              <TrialEndingBanner 
                displayMessage={trialEndingBannerProps.displayMessage}
                showButton={trialEndingBannerProps.showButton}
                onUpgradeClick={handleUpgradeClick}
              />
            )}
            <Switch>
              {getRoutes(filteredRoutes)}
              <Route path="/user/*">
                <Redirect to="/user/dashboard" />
              </Route>
            </Switch>
            <ScrollToTop />
          </div>
        </div>
      )}
    </OnboardingWrapper>
  );
};

const Admin = props => {
  return (
    <WeekConfigProvider selectedOrganization={props.selectedOrganization}>
      <AdminContent {...props} />
    </WeekConfigProvider>
  );
};

export default Admin;